import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { aiApi } from '@/services/api';
import LoadingSpinner from '@/components/UI/LoadingSpinner';

interface Message {
  id: string;
  content: string;
  isAI: boolean;
  timestamp: Date;
  model?: string;
}

interface RateLimit {
  requestsRemaining: number;
  resetTime: string;
  dailyLimit: number;
}

const AIAssistantPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [includeContext, setIncludeContext] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      isAI: false,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await aiApi.chat({
        message: userMessage.content,
        includeContext
      });

      if (response.data) {
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.data.response,
          isAI: true,
          timestamp: new Date(response.data.timestamp),
          model: response.data.model,
        };

        setMessages(prev => [...prev, aiMessage]);
        setRateLimit(response.data.rateLimit);
      }
    } catch (error: any) {
      console.error('AI chat error:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: error.response?.data?.message || 'Sorry, I encountered an error. Please try again.',
        isAI: true,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
      
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please wait before sending another message.');
      } else {
        toast.error('Failed to send message to AI assistant');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    toast.success('Chat cleared');
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="card border-b border-primary-custom pb-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <SparklesIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <div>
              <h1 className="text-heading-1 font-bold text-primary-custom">AI Trading Assistant</h1>
              <p className="text-body text-secondary-custom">Powered by Claude AI for trading insights and strategy recommendations</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Rate limit display */}
            {rateLimit && (
              <div className="text-caption text-tertiary-custom">
                <span className="font-medium">{rateLimit.requestsRemaining}</span>/{rateLimit.dailyLimit} requests remaining
              </div>
            )}
            
            {/* Context toggle */}
            <label className="flex items-center text-body text-secondary-custom">
              <input
                type="checkbox"
                checked={includeContext}
                onChange={(e) => setIncludeContext(e.target.checked)}
                className="mr-2 rounded"
              />
              Include trading context
            </label>
            
            {/* Clear chat button */}
            <button
              onClick={clearChat}
              className="text-body text-tertiary-custom hover-text-secondary-custom"
              disabled={messages.length === 0}
            >
              Clear Chat
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-tertiary-custom mx-auto mb-4" />
            <h3 className="text-heading-3 font-medium text-primary-custom mb-2">Start a conversation</h3>
            <p className="text-body text-secondary-custom max-w-md mx-auto">
              Ask me about trading strategies, market analysis, portfolio optimization, or any trading-related questions.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <button
                onClick={() => setInputMessage("Analyze my current portfolio performance")}
                className="p-3 text-left border border-secondary-custom card text-primary-custom rounded-lg hover-bg-secondary-custom text-body"
              >
                📊 Analyze my portfolio performance
              </button>
              <button
                onClick={() => setInputMessage("What trading strategies work best in volatile markets?")}
                className="p-3 text-left border border-secondary-custom card text-primary-custom rounded-lg hover-bg-secondary-custom text-body"
              >
                📈 Strategies for volatile markets
              </button>
              <button
                onClick={() => setInputMessage("How should I diversify my crypto portfolio?")}
                className="p-3 text-left border border-secondary-custom card text-primary-custom rounded-lg hover-bg-secondary-custom text-body"
              >
                🎯 Portfolio diversification tips
              </button>
              <button
                onClick={() => setInputMessage("Explain risk management in trading")}
                className="p-3 text-left border border-secondary-custom card text-primary-custom rounded-lg hover-bg-secondary-custom text-body"
              >
                🛡️ Risk management guidance
              </button>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isAI ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-3xl px-4 py-3 rounded-lg ${
                  message.isAI
                    ? 'bg-secondary-custom border border-secondary-custom'
                    : 'bg-indigo-600 text-white'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <span className={`text-caption font-medium ${
                    message.isAI ? 'text-secondary-custom' : 'text-indigo-100'
                  }`}>
                    {message.isAI ? 'AI Assistant' : 'You'}
                    {message.model && ` (${message.model})`}
                  </span>
                  <span className={`text-caption ${
                    message.isAI ? 'text-tertiary-custom' : 'text-indigo-200'
                  } ml-2`}>
                    <ClockIcon className="h-3 w-3 inline mr-1" />
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
                <div className={`text-body leading-relaxed ${
                  message.isAI ? 'text-primary-custom' : 'text-white'
                }`}>
                  {message.content.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < message.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-secondary-custom border border-secondary-custom px-4 py-3 rounded-lg">
              <div className="flex items-center text-secondary-custom">
                <LoadingSpinner size="sm" className="mr-2" />
                <span className="text-body">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="card border-t border-primary-custom p-4">
        <div className="flex space-x-3">
          <div className="flex-1">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about trading strategies, market analysis, or portfolio optimization..."
              className="form-input form-bg-custom form-border-custom form-text-custom form-placeholder-custom focus-border-custom focus-ring-custom w-full resize-none"
              rows={2}
              disabled={isLoading}
            />
          </div>
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {rateLimit && rateLimit.requestsRemaining < 5 && (
          <div className="mt-2 flex items-center text-caption text-warning-custom">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
            You have {rateLimit.requestsRemaining} requests remaining today
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistantPage; 